import { Alert, FormHelperText, Stack } from "@mui/material";
import { Box } from "@mui/system";
import { merge } from "lodash-es";
import { useRef } from "react";

import AppModalTitle from "@/common/components/Modals/AppModalTitle";
import { renderIf } from "@/common/helpers/render/renderIf";
import {
  TenantRequestDto,
  TenantRequestOperationResultInputDto,
  TenantRequestOperationType,
} from "@/core/api/generated/v1";

import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";
import { AppModalProps } from "../../Modals/AppModal";
import AppModalContent from "../../Modals/AppModalContent";
import AccessoryCheckCreateUpdate from "../AccessoryCheck/AccessoryCheckCreateUpdate";
import DamageCostEvaluationCreateUpdate from "../DamageCostEvaluation/DamageCostEvaluationCreateUpdate";
import DamageDetectionCreateUpdate from "../DamageDetection/DamageDetectionCreateUpdate";
import MaintenanceCreateUpdate from "../Maintenance/MaintenanceCreateUpdate";
import RepairOperationCreateUpdate from "../RepairOperation/RepairOperationCreateUpdate";
import VisualInspectionCreateUpdate from "../VisualInspection/VisualInspectionCreateUpdate";
import WashCreateUpdate from "../Wash/WashCreateUpdate";
import WheelOperationCreateUpdate from "../WheelOperation/WheelOperationCreateUpdate";
import { FormWrapperHandle } from "../components/BaseEntityCreateUpdate";
import { BaseEntityCreateUpdateModal } from "../components/BaseEntityCreateUpdateModal";
import TenantRequestLink from "./TenantRequestLink";

export interface Props extends AppModalProps {
  tenantRequest: TenantRequestDto;
  onSave?: (newValue: TenantRequestOperationResultInputDto) => void | Promise<void>;
}

export default function TenantRequestCreateOperationModal({
  tenantRequest,
  onSave,
  ...modalProps
}: Props) {
  const formWrapperRef = useRef<FormWrapperHandle | null>(null);

  const handleSave = async (newValue: Partial<TenantRequestOperationResultInputDto>) => {
    onSave &&
      (await onSave(
        merge(
          { operationType: tenantRequest?.content?.operation?.operationType },
          tenantRequest.result || {},
          newValue,
        ),
      ));
  };

  return (
    <BaseEntityCreateUpdateModal
      fullWidth
      maxWidth='xl'
      {...modalProps}
      formWrapperRef={formWrapperRef}
    >
      {(params) => (
        <>
          <AppModalTitle onClose={params.close}>
            Create new{" "}
            <strong>
              <InlineApiEnumValue
                type='TenantRequestOperationType'
                value={tenantRequest.content?.operation?.operationType}
                withDescription={false}
              />
            </strong>
          </AppModalTitle>

          <AppModalContent>
            <Stack spacing={2}>
              <Alert severity='info'>
                <Box>
                  You are creating a new{" "}
                  <strong>
                    <InlineApiEnumValue
                      type='TenantRequestOperationType'
                      value={tenantRequest.content?.operation?.operationType}
                      withDescription={false}
                    />
                  </strong>{" "}
                  operation for the company request{" "}
                  <strong>
                    <TenantRequestLink entity={tenantRequest} sx={{ display: "inline" }} />
                  </strong>
                  .
                </Box>
                <Box>You can only select damages that were included in the request.</Box>
              </Alert>

              <Box sx={{ py: 1 }}>
                {renderIf()
                  .if(
                    tenantRequest.content?.operation?.operationType ===
                      TenantRequestOperationType.VisualInspection,
                  )
                  .then(
                    <VisualInspectionCreateUpdate
                      ref={formWrapperRef}
                      defaultValues={{
                        vehicleId: tenantRequest.content?.operation?.visualInspection?.vehicleId,
                        tenantRequestsMeta: {
                          tenantRequestIds: [tenantRequest.id!],
                        },
                      }}
                      onSave={(newValue) =>
                        handleSave({ visualInspection: { visualInspectionId: newValue.id } })
                      }
                    />,
                  )
                  .elseif(
                    tenantRequest.content?.operation?.operationType ===
                      TenantRequestOperationType.DamageDetection,
                  )
                  .then(
                    <DamageDetectionCreateUpdate
                      ref={formWrapperRef}
                      defaultValues={{
                        vehicleId: tenantRequest.content?.operation?.damageDetection?.vehicleId,
                        tenantRequestsMeta: {
                          tenantRequestIds: [tenantRequest.id!],
                        },
                      }}
                      onSave={(newValue) =>
                        handleSave({ damageDetection: { damageDetectionId: newValue.id } })
                      }
                    />,
                  )
                  .elseif(
                    tenantRequest.content?.operation?.operationType ===
                      TenantRequestOperationType.DamageCostEvaluation,
                  )
                  .then(
                    <DamageCostEvaluationCreateUpdate
                      ref={formWrapperRef}
                      defaultValues={{
                        vehicleId:
                          tenantRequest.content?.operation?.damageCostEvaluation?.vehicleId,
                        vehicleDamageIds:
                          tenantRequest.content?.operation?.damageCostEvaluation?.vehicleDamageIds,
                        currency: tenantRequest.content?.operation?.damageCostEvaluation?.currency,
                        billTo: tenantRequest.billTo ? { ...tenantRequest.billTo } : undefined,
                        tenantRequestsMeta: {
                          tenantRequestIds: [tenantRequest.id!],
                        },
                      }}
                      displayProps={{
                        submitActionType: "saveAndComplete",
                      }}
                      defaultFormPerms={{
                        canEditVehicle: false,
                      }}
                      formSettings={{
                        // ensure only damages from the content can be selected
                        vehicleDamageIdsRestricted:
                          tenantRequest.content?.operation?.damageCostEvaluation?.vehicleDamageIds,
                      }}
                      onSave={(newValue) =>
                        handleSave({
                          damageCostEvaluation: { damageCostEvaluationId: newValue.id },
                        })
                      }
                    />,
                  )
                  .elseif(
                    tenantRequest.content?.operation?.operationType ===
                      TenantRequestOperationType.AccessoryCheck,
                  )
                  .then(
                    <AccessoryCheckCreateUpdate
                      ref={formWrapperRef}
                      defaultValues={{
                        vehicleId: tenantRequest.content?.operation?.accessoryCheck?.vehicleId,
                        tenantRequestsMeta: {
                          tenantRequestIds: [tenantRequest.id!],
                        },
                      }}
                      onSave={(newValue) =>
                        handleSave({ accessoryCheck: { accessoryCheckId: newValue.id } })
                      }
                    />,
                  )
                  .elseif(
                    tenantRequest.content?.operation?.operationType ===
                      TenantRequestOperationType.RepairOperation,
                  )
                  .then(
                    <RepairOperationCreateUpdate
                      ref={formWrapperRef}
                      defaultValues={{
                        vehicleId: tenantRequest.content?.operation?.repairOperation?.vehicleId,
                        vehicleDamageIds:
                          tenantRequest.content?.operation?.repairOperation?.vehicleDamageIds,
                        currency: tenantRequest.content?.operation?.repairOperation?.currency,
                        tenantRequestsMeta: {
                          tenantRequestIds: [tenantRequest.id!],
                        },
                      }}
                      onSave={(newValue) =>
                        handleSave({ repairOperation: { repairOperationId: newValue.id } })
                      }
                    />,
                  )
                  .elseif(
                    tenantRequest.content?.operation?.operationType ===
                      TenantRequestOperationType.WheelOperation,
                  )
                  .then(
                    <WheelOperationCreateUpdate
                      ref={formWrapperRef}
                      defaultValues={{
                        vehicleId: tenantRequest.content?.operation?.wheelOperation?.vehicleId,
                        currency: tenantRequest.content?.operation?.wheelOperation?.currency,
                        tenantRequestsMeta: {
                          tenantRequestIds: [tenantRequest.id!],
                        },
                      }}
                      onSave={(newValue) =>
                        handleSave({ wheelOperation: { wheelOperationId: newValue.id } })
                      }
                    />,
                  )
                  .elseif(
                    tenantRequest.content?.operation?.operationType ===
                      TenantRequestOperationType.Wash,
                  )
                  .then(
                    <WashCreateUpdate
                      ref={formWrapperRef}
                      defaultValues={{
                        vehicleId: tenantRequest.content?.operation?.wash?.vehicleId,
                        currency: tenantRequest.content?.operation?.wash?.currency,
                        tenantRequestsMeta: {
                          tenantRequestIds: [tenantRequest.id!],
                        },
                      }}
                      onSave={(newValue) => handleSave({ wash: { washId: newValue.id } })}
                    />,
                  )
                  .elseif(
                    tenantRequest.content?.operation?.operationType ===
                      TenantRequestOperationType.Maintenance,
                  )
                  .then(
                    <MaintenanceCreateUpdate
                      ref={formWrapperRef}
                      defaultValues={{
                        vehicleId: tenantRequest.content?.operation?.maintenance?.vehicleId,
                        currency: tenantRequest.content?.operation?.maintenance?.currency,
                        tenantRequestsMeta: {
                          tenantRequestIds: [tenantRequest.id!],
                        },
                      }}
                      onSave={(newValue) =>
                        handleSave({ maintenance: { maintenanceId: newValue.id } })
                      }
                    />,
                  )
                  .else(
                    <FormHelperText error>
                      <InlineApiEnumValue
                        type='TenantRequestType'
                        value={tenantRequest?.content?.type}
                      />{" "}
                      not supported yet!
                    </FormHelperText>,
                  )
                  .render()}
              </Box>
            </Stack>
          </AppModalContent>
        </>
      )}
    </BaseEntityCreateUpdateModal>
  );
}
